import { useState } from 'react';
import {
  DeleteOutlined, EyeOutlined, IdcardOutlined, ReloadOutlined, SearchOutlined, StopOutlined, WarningOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader, TabsTruot } from '../../../components/shared';
import { Badge, Button, Empty, Input, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import {
  LOAI_GIAY_TO, MUC_RUI_RO, QUYEN, QUYET_DINH, TAB_HO_SO, TEN_TAB_HO_SO, TRANG_THAI_PHIEN,
} from '../constants';
import { useDanhSachPhien, useThaoTacHoSo } from '../hooks/useEkyc';
import { huyDuoc, xoaDuoc } from '../nghiep-vu/ekyc';
import type { PhienTomTat, TabHoSo } from '../types';

/**
 * Hồ sơ định danh (`/ekyc/ho-so`, phân hệ `EKYC_SESSION`).
 *
 * Dựng theo giao diện v3 (ảnh người dùng cung cấp 24/10/2026): tab ngang thay ô chọn trạng thái,
 * mã hồ sơ ở cột đầu với loại giấy tờ và mã app ở dòng phụ.
 *
 * Ba điều màn này cố ý làm khác lối thường gặp:
 *
 * 1. **Mặc định mở tab "Chờ người", không phải "Tất cả".** Đó là việc người vận hành làm được
 *    ngay; "Tất cả" là một danh sách không có hành động nào gắn với nó.
 * 2. **Ô tìm chỉ khớp `referenceId`.** Tìm theo tên hay số giấy tờ xuyên mọi đối tác "đúng là thứ
 *    không nên tồn tại" (`02` §7.1) — nó biến màn quản trị thành một công cụ tra cứu người.
 * 3. **Tab "Rơi" không chứa "Từ chối".** "Người dùng bỏ dở" và "hệ thống từ chối" khác hẳn nhau
 *    khi đo tỉ lệ rơi (`08` §6), và trộn hai thứ làm mất đúng con số người vận hành cần.
 *
 * Menu `⋮` của mỗi dòng (ADR-UI-0005): xem, huỷ hồ sơ đang chạy, xoá dữ liệu cá nhân của hồ sơ đã chốt. "Xoá" là xoá
 * **dữ liệu cá nhân** — ảnh, dữ liệu giấy tờ, vector sinh trắc; dòng hồ sơ ở lại với trạng thái "Đã xoá" làm bằng
 * chứng (Nghị định 13/2023/NĐ-CP), nên nhãn nói đúng thứ bị xoá thay vì "Xoá hồ sơ".
 *
 * ⚠️ `GET /sessions` đọc **xuyên tenant** — hồ sơ định danh của **mọi** đối tác. Đó là lý do mô tả
 * màn nói thẳng rằng mỗi lần mở đều được ghi vào nhật ký (`02` §7.1).
 */
export function HoSoDinhDanhView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const toast = useToast();
  const quyen = useModulePermissions(QUYEN);
  const thaoTac = useThaoTacHoSo();
  const [hoiHuy, setHoiHuy] = useState<PhienTomTat | null>(null);
  const [hoiXoa, setHoiXoa] = useState<PhienTomTat | null>(null);

  const [tab, setTab] = useState<TabHoSo>('cho-nguoi');
  const [nhap, setNhap] = useState('');
  const [timKiem, setTimKiem] = useState('');

  const q = useDanhSachPhien(tab, timKiem);
  const tieuDe = t('ekyc.hoSoTieuDe');
  const coLoc = timKiem.length > 0;
  const loiTaiDau = q.isError && q.dong.length === 0;

  const cot = [
    {
      title: t('ekyc.cotHoSo'),
      dataIndex: 'referenceId',
      key: 'referenceId',
      render: (_: unknown, dong: PhienTomTat) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-content-primary">{tenHoSo(dong)}</p>
          <p className="truncate text-2xs text-content-muted">
            {t(LOAI_GIAY_TO[dong.documentType] ?? 'ekyc.loaiGiayTo.VIETNAM_CCCD')} · {dong.appId}
          </p>
        </div>
      ),
    },
    {
      title: t('chung.trangThai'),
      dataIndex: 'status',
      key: 'status',
      render: (_: unknown, dong: PhienTomTat) => {
        const tong = TRANG_THAI_PHIEN[dong.status];
        return <Badge variant={tong.variant}>{t(tong.khoa)}</Badge>;
      },
    },
    {
      title: t('ekyc.cotQuyetDinh'),
      dataIndex: 'decision',
      key: 'decision',
      render: (_: unknown, dong: PhienTomTat) => {
        // `null` ở đây là **chưa có quyết định**, không phải "đạt" hay "trượt" — gạch dài nói đúng điều đó.
        if (dong.decision === null) return <span className="text-content-muted">{KHONG_CO}</span>;
        const tong = QUYET_DINH[dong.decision];
        return <Badge variant={tong.variant}>{t(tong.khoa)}</Badge>;
      },
    },
    {
      title: t('ekyc.cotRuiRo'),
      dataIndex: 'riskScore',
      key: 'riskScore',
      render: (_: unknown, dong: PhienTomTat) => {
        if (dong.riskLevel === null || dong.riskScore === null) {
          return <span className="text-content-muted">{KHONG_CO}</span>;
        }
        const tong = MUC_RUI_RO[dong.riskLevel];
        return (
          <Badge variant={tong.variant}>
            {t(tong.khoa)} · {soVN(Math.round(dong.riskScore * 100) / 100, locale)}
          </Badge>
        );
      },
    },
    {
      title: t('ekyc.cotTaoLuc'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: unknown, dong: PhienTomTat) => (
        <span className="whitespace-nowrap text-content-secondary">{ngayGioVN(dong.createdAt)}</span>
      ),
    },
    {
      title: t('chung.thaoTac'),
      key: 'thaoTac',
      width: 72,
      align: 'center' as const,
      render: (_: unknown, dong: PhienTomTat) => (
        // Chặn sự kiện tới dòng: bấm menu (kể cả mục trong popup, nổi bọt qua portal) không được mở luôn trang chi tiết.
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <MenuThaoTac
            tenBanGhi={tenHoSo(dong)}
            muc={[
              {
                khoa: 'xem', nhan: t('ekyc.xemChiTiet'), icon: <EyeOutlined />, nhom: 'xem',
                onChon: () => dieuHuong(`/ekyc/ho-so/${dong.id}`),
              },
              {
                khoa: 'huy', nhan: t('ekyc.huyHoSo'), icon: <StopOutlined />, nhom: 'sua',
                hien: quyen.phienSua && huyDuoc(dong.status),
                onChon: () => setHoiHuy(dong),
              },
              {
                khoa: 'xoa', nhan: t('ekyc.xoaDuLieu'), icon: <DeleteOutlined />, nhom: 'pha-huy',
                // Đã xoá thì không còn gì để xoá: ẩn. Đang chạy thì có quyền nhưng phải huỷ trước — nói lý do.
                hien: quyen.phienXoa && dong.status !== 'DELETED',
                voHieu: !xoaDuoc(dong.status),
                moTa: xoaDuoc(dong.status) ? undefined : t('ekyc.xoaPhaiHuyTruoc'),
                onChon: () => setHoiXoa(dong),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.dinhDanhEkyc') }, { label: tieuDe }]}
          titleIcon={<IdcardOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('ekyc.hoSoMo')}
          actions={
            <>
              <Input
                value={nhap}
                onChange={(e) => setNhap(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setTimKiem(nhap.trim());
                }}
                onBlur={() => setTimKiem(nhap.trim())}
                placeholder={t('ekyc.timHoSo')}
                aria-label={t('ekyc.timHoSo')}
                prefix={<SearchOutlined className="text-content-muted" />}
                className="w-72"
              />
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void q.refetch()}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
            </>
          }
        />

        <div className="rounded-lg border border-default bg-surface shadow-sm">
          <div className="px-4 pt-2">
            <TabsTruot
              gon
              defaultActiveKey={tab}
              onChange={(key) => setTab(key as TabHoSo)}
              items={TAB_HO_SO.map((ma) => ({ key: ma, label: t(TEN_TAB_HO_SO[ma]) }))}
            />
          </div>

          <div className="p-4 pt-0">
            {q.isLoading ? (
              <div className="space-y-2 pt-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : loiTaiDau ? (
              <div role="alert" className="flex flex-wrap items-center gap-3 rounded border border-danger-200 bg-danger-50 px-3 py-2.5">
                <WarningOutlined className="shrink-0 text-danger-600" />
                <span className="min-w-0 text-sm text-danger-700">
                  {t('ekyc.loiDanhSach', { loi: dichLoi(q.error) })}
                </span>
                <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                  {t('chung.thuLai')}
                </Button>
              </div>
            ) : q.dong.length === 0 ? (
              /*
               * Rỗng **vì bộ lọc** thì bỏ cả mô tả lẫn nút hành động (§10.1 của hệ thiết kế): mời
               * "tạo cái đầu tiên" khi người dùng vừa lọc hết là sai. Lọc ở đây gồm cả tab, không
               * chỉ ô tìm — đứng ở tab "Từ chối" mà không có hồ sơ nào là **tin tốt**.
               */
              coLoc || tab !== 'tat-ca' ? (
                <Empty icon={SearchOutlined} title={t('ekyc.rongLoc')} />
              ) : (
                <Empty icon={IdcardOutlined} title={t('ekyc.rong')} description={t('ekyc.rongMo')} />
              )
            ) : (
              <>
                <DataTable<PhienTomTat>
                  caption={t('ekyc.bangHoSo')}
                  columns={cot}
                  dataSource={q.dong}
                  pagination={false}
                  onRow={(dong) => ({
                    onClick: () => dieuHuong(`/ekyc/ho-so/${dong.id}`),
                    className: 'cursor-pointer',
                    tabIndex: 0,
                    'aria-label': t('ekyc.xemChiTiet'),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        dieuHuong(`/ekyc/ho-so/${dong.id}`);
                      }
                    },
                  })}
                />

                {q.hasNextPage && (
                  <div className="mt-3 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={q.isFetchingNextPage}
                      onClick={() => void q.fetchNextPage()}
                    >
                      {q.isFetchingNextPage ? t('ekyc.dangTaiThem') : t('ekyc.taiThem')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <HopXacNhanThaoTac
        open={hoiHuy !== null}
        tieuDe={t('ekyc.huyHoiTieuDe', { ma: hoiHuy ? tenHoSo(hoiHuy) : '' })}
        than={t('ekyc.huyHoiThan')}
        nhanXacNhan={t('ekyc.huyHoSo')}
        nguyHiem
        dangChay={thaoTac.huy.isPending}
        loi={thaoTac.huy.isError ? dichLoi(thaoTac.huy.error) : null}
        onClose={() => {
          setHoiHuy(null);
          thaoTac.huy.reset();
        }}
        onXacNhan={() => {
          if (!hoiHuy) return;
          const ma = tenHoSo(hoiHuy);
          thaoTac.huy.mutate(hoiHuy.id, {
            onSuccess: () => {
              toast.success(t('ekyc.daHuy', { ma }));
              setHoiHuy(null);
            },
          });
        }}
      />

      <HopXacNhanThaoTac
        open={hoiXoa !== null}
        tieuDe={t('ekyc.xoaHoiTieuDe', { ma: hoiXoa ? tenHoSo(hoiXoa) : '' })}
        than={t('ekyc.xoaHoiThan')}
        nhanXacNhan={t('ekyc.xoaDuLieu')}
        nguyHiem
        dangChay={thaoTac.xoa.isPending}
        loi={thaoTac.xoa.isError ? dichLoi(thaoTac.xoa.error) : null}
        onClose={() => {
          setHoiXoa(null);
          thaoTac.xoa.reset();
        }}
        onXacNhan={() => {
          if (!hoiXoa) return;
          const ma = tenHoSo(hoiXoa);
          thaoTac.xoa.mutate(hoiXoa.id, {
            onSuccess: () => {
              toast.success(t('ekyc.daXoa', { ma }));
              setHoiXoa(null);
            },
          });
        }}
      />
    </div>
  );
}

/** Tên hiển thị của một hồ sơ: mã của đối tác nếu có, không thì id — đúng thứ cột "Hồ sơ" in đậm. */
function tenHoSo(p: PhienTomTat): string {
  return p.referenceId || p.id;
}
